'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Products', href: '/#products' },
  { label: 'Travel', href: '/#travel' },
  { label: 'Rewards', href: '/#rewards' },
  { label: 'For Families', href: '/#family' },
  { label: 'Pricing', href: '/#pricing' },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
          <AppLogo size={32} />
          <span
            className={`font-extrabold text-xl tracking-tight transition-colors duration-300 ${
              scrolled ? 'text-primary' : 'text-white'
            }`}
          >
            RETURNI
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks?.map((link) => (
            <Link
              key={`nav-${link?.label}`}
              href={link?.href}
              className={`px-4 py-2 rounded-full text-sm font-500 transition-all duration-200 hover:bg-white/10 ${
                scrolled
                  ? 'text-foreground hover:bg-secondary hover:text-primary'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/user-dashboard"
            className={`text-sm font-600 px-4 py-2 rounded-full transition-all duration-200 ${
              scrolled
                ? 'text-foreground hover:text-accent'
                : 'text-white/90 hover:text-white'
            }`}
          >
            Sign In
          </Link>
          <Link
            href="/user-dashboard"
            className="btn-primary text-sm px-5 py-2.5"
          >
            Protect My Stuff
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-2 rounded-lg transition-colors ${
            scrolled ? 'text-foreground' : 'text-white'
          }`}
          aria-label="Toggle mobile menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border shadow-lg">
          <nav className="px-6 py-4 flex flex-col gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm font-500 text-foreground rounded-xl hover:bg-secondary transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="mt-4 flex flex-col gap-2 pt-4 border-t border-border">
              <Link
                href="/user-dashboard"
                onClick={() => setMobileOpen(false)}
                className="btn-outline-dark text-sm px-5 py-3 w-full justify-center"
              >
                Sign In
              </Link>
              <Link
                href="/user-dashboard"
                onClick={() => setMobileOpen(false)}
                className="btn-primary text-sm px-5 py-3 w-full justify-center"
              >
                Protect My Stuff
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}